// UI wiring. Everything here is instrumentation over the engine: it renders
// engine state and events, and issues real put/get/delete/scan calls. There
// is no animation state that isn't derived from an actual engine operation.

import { LSM } from "../engine/lsm";
import { EngineEvent } from "../engine/events";
import { TableMeta, EngineConfig } from "../engine/types";
import { Workload, Op } from "../engine/workload";
import { Trace } from "../engine/trace";
import { renderDocs, openDocsAt, closeDocs } from "./docsview";
import { renderTraceSteps } from "./tracelist";

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`missing element ${sel}`);
  return el as T;
};

const fmtBytes = (n: number): string =>
  n >= 1 << 20
    ? (n / (1 << 20)).toFixed(1) + " MB"
    : n >= 1024
      ? (n / 1024).toFixed(1) + " KB"
      : n + " B";

export class App {
  private engine: LSM;
  private unsub: () => void = () => {};
  private workloadTimer: number | null = null;
  private workload: Workload | null = null;
  private activeWl: string | null = null;
  private compactingIds = new Set<number>();
  private selectedTable: number | null = null;
  // Rolling window for read amplification: probes per get, last 200 gets.
  private raWindow: { probes: number; skips: number }[] = [];
  private lastGetSnapshot = { gets: 0, probed: 0, skips: 0 };
  private spaceAmpTimer: number | null = null;
  private renderQueued = false;
  /** When pinned, the trace panel stops following new operations. */
  private tracePinned = false;
  private pendingTrace: Trace | null = null;
  private traceFrame = false;

  constructor(engine: LSM, private readonly reopen: () => Promise<LSM>) {
    this.engine = engine;
    this.attach(engine);
    this.bindControls();
    renderDocs();
    this.syncKnobs();
    this.renderAll();
    this.scheduleSpaceAmp();
    window.setInterval(() => this.renderMetrics(), 500);
  }

  get db(): LSM {
    return this.engine;
  }

  // ------------------------------------------------------------ engine glue

  private attach(engine: LSM): void {
    this.unsub();
    this.engine = engine;
    this.unsub = engine.events.on((e) => this.onEvent(e));
  }

  private onEvent(e: EngineEvent): void {
    switch (e.type) {
      case "memtable":
        this.renderMemtable(e.bytes, e.count, e.limit);
        this.refreshWal();
        break;
      case "flush-start":
        this.setActivity(`flushing ${e.entryCount} entries…`);
        break;
      case "flush-end":
        this.log(`flush → L0 sst#${e.table.id} (${fmtBytes(e.table.size)}, ${e.table.entryCount} entries)`);
        this.setActivity("");
        this.queueRender();
        break;
      case "compaction-start":
        for (const id of e.inputTables) this.compactingIds.add(id);
        this.setActivity(`compacting L${e.fromLevel}→L${e.toLevel} (${e.strategy}, ${e.inputTables.length} runs)…`);
        this.queueRender();
        break;
      case "compaction-progress":
        this.setActivity(`compacting… ${e.done}/${e.total} entries merged`);
        break;
      case "compaction-end": {
        for (const id of e.inputTables) this.compactingIds.delete(id);
        const outs = e.outputTables.map((t) => "#" + t.id).join(",");
        this.log(
          `compact L${e.fromLevel}→L${e.toLevel}: ${e.inputTables.length} runs → ${e.outputTables.length} (${outs}), ` +
            `${fmtBytes(e.bytesWritten)} written` +
            (e.droppedTombstones ? `, ${e.droppedTombstones} tombstones dropped` : ""),
        );
        this.setActivity("");
        this.queueRender();
        this.scheduleSpaceAmp();
        break;
      }
      case "manifest":
        this.queueRender();
        break;
      case "trace":
        this.showTrace(e.trace);
        break;
      case "recovered":
        this.log(`recovered: replayed ${e.walRecords} WAL records, ${e.tables} SSTables on disk`);
        this.queueRender();
        break;
    }
  }

  // --------------------------------------------------------------- controls

  private bindControls(): void {
    const keyIn = $<HTMLInputElement>("#in-key");
    const valIn = $<HTMLInputElement>("#in-value");

    $("#btn-put").onclick = async () => {
      if (!keyIn.value) return this.echo("put: key required");
      await this.engine.put(keyIn.value, valIn.value);
      this.echo(`put ${keyIn.value} = "${valIn.value}"`);
    };
    $("#btn-get").onclick = async () => {
      if (!keyIn.value) return this.echo("get: key required");
      const t0 = performance.now();
      const before = { ...this.engine.metrics };
      const v = await this.engine.get(keyIn.value);
      const m = this.engine.metrics;
      const probed = m.tablesProbed - before.tablesProbed;
      const skipped = m.bloomSkips - before.bloomSkips;
      this.echo(
        `get ${keyIn.value} → ${v === null ? "(not found)" : `"${v}"`}  ` +
          `[${probed} probed, ${skipped} bloom-skipped, ${(performance.now() - t0).toFixed(1)}ms]`,
      );
    };
    $("#btn-del").onclick = async () => {
      if (!keyIn.value) return this.echo("delete: key required");
      await this.engine.delete(keyIn.value);
      this.echo(`delete ${keyIn.value} (tombstone written)`);
    };
    $("#btn-scan").onclick = async () => {
      const lo = keyIn.value || "";
      const hi = valIn.value || "￿";
      const rows = await this.engine.scan(lo, hi, 20);
      this.echo(
        `scan [${lo || "-∞"}, ${hi === "￿" ? "∞" : hi}) → ${rows.length} rows\n` +
          rows.map(([k, v]) => `  ${k} = "${v.length > 24 ? v.slice(0, 24) + "…" : v}"`).join("\n"),
      );
    };

    for (const btn of document.querySelectorAll<HTMLButtonElement>(".wl")) {
      btn.onclick = () => this.toggleWorkload(btn.dataset.wl!, btn);
    }
    $("#btn-stop").onclick = () => this.stopWorkload();
    $("#btn-burst").onclick = () => this.burst();

    $("#btn-crash").onclick = async () => {
      this.stopWorkload();
      const snap = this.engine.snapshot();
      const dropped = snap.memtable.count;
      this.log(`💥 crash: dropping memtable (${dropped} entries) without flushing`);
      const fresh = await this.reopen();
      this.attach(fresh);
      this.renderAll();
      this.log(`engine reopened — WAL replay restored acknowledged writes`);
    };
    $("#btn-reset").onclick = async () => {
      this.stopWorkload();
      await this.engine.reset();
      this.raWindow = [];
      this.lastGetSnapshot = { gets: 0, probed: 0, skips: 0 };
      $("#event-log").textContent = "";
      this.log("database wiped");
      this.renderAll();
    };
    $("#inspector-close").onclick = () => {
      this.selectedTable = null;
      $("#inspector").hidden = true;
      this.queueRender();
    };

    // Docs: the top-bar button opens at the overview, every "?" opens the
    // overlay scrolled to the section describing that component.
    $("#btn-docs").onclick = () => openDocsAt("overview");
    $("#docs-close").onclick = () => closeDocs();
    $("#docs-overlay").onclick = (ev) => {
      if (ev.target === $("#docs-overlay")) closeDocs();
    };
    document.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") closeDocs();
    });
    for (const btn of document.querySelectorAll<HTMLButtonElement>(".info")) {
      btn.onclick = (ev) => {
        ev.stopPropagation();
        openDocsAt(btn.dataset.doc!);
      };
    }
    const pin = $<HTMLInputElement>("#trace-pin");
    pin.onchange = () => {
      this.tracePinned = pin.checked;
    };

    // Knobs — these change the running engine's config; triggers re-evaluate.
    const memKnob = $<HTMLInputElement>("#knob-memtable");
    memKnob.oninput = () => {
      this.engine.setConfig({ memtableBytes: Number(memKnob.value) });
      this.syncKnobs();
    };
    const fanKnob = $<HTMLInputElement>("#knob-fanout");
    fanKnob.oninput = () => {
      this.engine.setConfig({ fanout: Number(fanKnob.value) });
      this.syncKnobs();
    };
    $<HTMLSelectElement>("#knob-strategy").onchange = (ev) => {
      const v = (ev.target as HTMLSelectElement).value as EngineConfig["compaction"];
      this.engine.setConfig({ compaction: v });
      this.log(`compaction strategy → ${v}`);
      this.queueRender();
    };
    const paceKnob = $<HTMLInputElement>("#knob-pace");
    paceKnob.oninput = () => {
      this.engine.compactionDelayMs = Number(paceKnob.value);
      this.syncKnobs();
    };
  }

  private syncKnobs(): void {
    const c = this.engine.config;
    $<HTMLInputElement>("#knob-memtable").value = String(c.memtableBytes);
    $("#lbl-memtable").textContent = fmtBytes(c.memtableBytes);
    $<HTMLInputElement>("#knob-fanout").value = String(c.fanout);
    $("#lbl-fanout").textContent = "×" + c.fanout;
    $<HTMLSelectElement>("#knob-strategy").value = c.compaction;
    $<HTMLInputElement>("#knob-pace").value = String(this.engine.compactionDelayMs);
    $("#lbl-pace").textContent = this.engine.compactionDelayMs + "ms";
  }

  // -------------------------------------------------------------- workloads

  private toggleWorkload(kind: string, btn: HTMLButtonElement): void {
    if (this.activeWl === kind) return this.stopWorkload();
    this.stopWorkload();
    this.activeWl = kind;
    btn.classList.add("active");
    $<HTMLButtonElement>("#btn-stop").disabled = false;
    const seed = Number($<HTMLInputElement>("#in-seed").value) || 42;
    this.workload = new Workload(seed);
    this.log(`workload "${kind}" started (seed ${seed})`);
    const tick = async () => {
      const rate = Math.min(500, Math.max(1, Number($<HTMLInputElement>("#in-rate").value) || 40));
      const w = this.workload!;
      const op: Op =
        kind === "seq" ? w.sequentialPut()
        : kind === "rand" ? w.randomPut()
        : kind === "zipf" ? w.zipfianGet()
        : w.mixed(1)[0];
      await this.applyOp(op);
      if (this.activeWl === kind) {
        this.workloadTimer = window.setTimeout(tick, 1000 / rate);
      }
    };
    tick();
  }

  private stopWorkload(): void {
    if (this.workloadTimer !== null) window.clearTimeout(this.workloadTimer);
    this.workloadTimer = null;
    this.activeWl = null;
    this.workload = null;
    for (const b of document.querySelectorAll(".wl")) b.classList.remove("active");
    $<HTMLButtonElement>("#btn-stop").disabled = true;
  }

  private async burst(): Promise<void> {
    const seed = Number($<HTMLInputElement>("#in-seed").value) || 42;
    const w = new Workload(seed + 1000);
    const btn = $<HTMLButtonElement>("#btn-burst");
    btn.disabled = true;
    this.log("write burst: 500 random puts…");
    const t0 = performance.now();
    for (let i = 0; i < 500; i++) {
      const op = w.randomPut();
      await this.engine.put(op.key, (op as { value: string }).value);
    }
    this.log(`burst done in ${((performance.now() - t0) / 1000).toFixed(2)}s`);
    btn.disabled = false;
  }

  private async applyOp(op: Op): Promise<void> {
    if (op.op === "put") await this.engine.put(op.key, op.value);
    else if (op.op === "delete") await this.engine.delete(op.key);
    else await this.engine.get(op.key);
  }

  // ---------------------------------------------------------------- render

  private queueRender(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      this.renderTree();
      this.renderMetrics();
    });
  }

  private renderAll(): void {
    const snap = this.engine.snapshot();
    this.renderMemtable(snap.memtable.bytes, snap.memtable.count, snap.memtable.limit);
    this.renderTree();
    this.renderMetrics();
    this.refreshWal();
    this.syncKnobs();
  }

  private renderMemtable(bytes: number, count: number, limit: number): void {
    const pct = Math.min(100, (bytes / limit) * 100);
    const fill = $("#memtable-fill");
    fill.style.width = pct + "%";
    fill.classList.toggle("hot", pct > 75);
    $("#memtable-label").textContent =
      `${count} keys · ${fmtBytes(bytes)} / ${fmtBytes(limit)}`;
  }

  private async refreshWal(): Promise<void> {
    const n = await this.engine.walRecordCount();
    $("#wal-count").textContent = String(n);
  }

  private renderTree(): void {
    const snap = this.engine.snapshot();
    const host = $("#levels");
    host.textContent = "";
    snap.levels.forEach((tables, i) => {
      const row = document.createElement("div");
      row.className = "level-row";
      const label = document.createElement("span");
      label.className = "row-label";
      label.textContent = "L" + i;
      row.appendChild(label);
      const box = document.createElement("div");
      box.className = "level-tables";
      for (const t of tables) {
        box.appendChild(this.tableEl(t));
      }
      const meta = document.createElement("span");
      meta.className = "level-meta";
      const size = tables.reduce((s, t) => s + t.size, 0);
      meta.textContent = tables.length
        ? `${tables.length} run${tables.length > 1 ? "s" : ""} · ${fmtBytes(size)}`
        : "empty";
      box.appendChild(meta);
      row.appendChild(box);
      host.appendChild(row);
    });
  }

  private tableEl(t: TableMeta): HTMLElement {
    const el = document.createElement("div");
    el.className = "sst";
    if (this.compactingIds.has(t.id)) el.classList.add("compacting");
    if (this.selectedTable === t.id) el.classList.add("selected");
    el.innerHTML = `#${t.id} · ${fmtBytes(t.size)}<span class="range">${esc(short(t.minKey))} … ${esc(short(t.maxKey))}</span>`;
    el.onclick = () => this.inspect(t);
    return el;
  }

  private async inspect(t: TableMeta): Promise<void> {
    this.selectedTable = t.id;
    const reader = await this.engine.tableReader(t.id);
    const bitsPreview = renderBloomPreview(reader);
    $("#inspector-body").innerHTML = `
      <h3>SSTable #${t.id} (L${t.level})</h3>
      <table>
        <tr><td>key range</td><td>${esc(t.minKey)} … ${esc(t.maxKey)}</td></tr>
        <tr><td>size</td><td>${fmtBytes(t.size)} (${t.size} B serialized)</td></tr>
        <tr><td>entries</td><td>${t.entryCount} (${t.tombstoneCount} tombstones)</td></tr>
        <tr><td>sparse index</td><td>${reader.sparseIndexSize} entries</td></tr>
        <tr><td>bloom</td><td>${reader.bloomBits} bits, ${reader.bloomHashes} hashes</td></tr>
        <tr><td>bloom bits</td><td><div class="bloom-bits">${bitsPreview}</div></td></tr>
      </table>`;
    $("#inspector").hidden = false;
    this.queueRender();
  }

  /**
   * Render the steps an operation actually took. Traces can arrive faster
   * than the display refreshes under load, so keep only the newest and paint
   * it once per frame.
   */
  private showTrace(trace: Trace): void {
    if (this.tracePinned) return;
    this.pendingTrace = trace;
    if (this.traceFrame) return;
    this.traceFrame = true;
    requestAnimationFrame(() => {
      this.traceFrame = false;
      const t = this.pendingTrace;
      this.pendingTrace = null;
      if (!t || this.tracePinned) return;
      $("#trace-title").textContent =
        `— ${t.op}${t.arg ? `("${short(t.arg)}")` : ""} · ${t.steps.length} steps · ${t.ms.toFixed(2)}ms`;
      renderTraceSteps($("#trace-steps"), t);
      $("#trace-summary").textContent = "⇒ " + t.summary;
    });
  }

  private renderMetrics(): void {
    const m = this.engine.metrics;

    // Write amplification: physical SSTable bytes per logical byte written.
    const physical = m.flushBytesWritten + m.compactionBytesWritten;
    const wa = m.userBytesWritten > 0 ? physical / m.userBytesWritten : 0;
    $("#m-wa").textContent = wa ? wa.toFixed(2) + "×" : "–";

    // Read amplification: rolling average of tables probed per get.
    const dGets = m.gets - this.lastGetSnapshot.gets;
    if (dGets > 0) {
      this.raWindow.push({
        probes: (m.tablesProbed - this.lastGetSnapshot.probed) / dGets,
        skips: (m.bloomSkips - this.lastGetSnapshot.skips) / dGets,
      });
      if (this.raWindow.length > 200) this.raWindow.splice(0, this.raWindow.length - 200);
      this.lastGetSnapshot = { gets: m.gets, probed: m.tablesProbed, skips: m.bloomSkips };
    }
    if (this.raWindow.length) {
      const ra = this.raWindow.reduce((s, x) => s + x.probes, 0) / this.raWindow.length;
      $("#m-ra").textContent = ra.toFixed(2);
    }
    const bloomPct = m.tablesProbed > 0 ? (100 * m.bloomSkips) / m.tablesProbed : 0;
    $("#m-bloom").textContent = m.tablesProbed ? bloomPct.toFixed(0) + "%" : "–";

    $("#counters").innerHTML =
      `puts <b>${m.puts}</b> · deletes <b>${m.deletes}</b> · gets <b>${m.gets}</b><br>` +
      `flushes <b>${m.flushes}</b> · compactions <b>${m.compactions}</b> · tombstones dropped <b>${m.droppedTombstones}</b><br>` +
      `WAL written <b>${fmtBytes(m.walBytesWritten)}</b> · SST written <b>${fmtBytes(m.flushBytesWritten + m.compactionBytesWritten)}</b><br>` +
      `probes <b>${m.tablesProbed}</b> · bloom skips <b>${m.bloomSkips}</b> · wasted probes <b>${m.wastedProbes}</b>`;

    // Per-level bars against the leveled target size.
    const snap = this.engine.snapshot();
    const host = $("#level-sizes");
    host.textContent = "";
    snap.levels.forEach((tables, i) => {
      const size = tables.reduce((s, t) => s + t.size, 0);
      const target = i === 0
        ? this.engine.config.l0Trigger * this.engine.config.memtableBytes
        : this.engine.levelTargetBytes(i);
      const row = document.createElement("div");
      row.className = "lvl-bar-row";
      const pct = Math.min(100, (size / target) * 100);
      row.innerHTML =
        `<span class="name">L${i}</span>` +
        `<div class="lvl-bar ${size > target ? "over" : ""}"><div style="width:${pct}%"></div></div>` +
        `<span class="val">${fmtBytes(size)} / ${fmtBytes(target)}</span>`;
      host.appendChild(row);
    });
  }

  /** Space amp needs a full scan through the real read path — throttle it. */
  private scheduleSpaceAmp(): void {
    if (this.spaceAmpTimer !== null) return;
    this.spaceAmpTimer = window.setTimeout(async () => {
      this.spaceAmpTimer = null;
      const disk = this.engine.totalTableBytes();
      if (disk === 0) {
        $("#m-sa").textContent = "–";
        return;
      }
      const live = await this.engine.measureLiveBytes();
      const sa = live.bytes > 0 ? disk / live.bytes : 0;
      $("#m-sa").textContent = sa ? sa.toFixed(2) + "×" : "–";
      $("#m-sa").title = `${fmtBytes(disk)} on disk / ${fmtBytes(live.bytes)} live (${live.keys} keys)`;
    }, 1500);
  }

  // ------------------------------------------------------------------ log

  private echo(msg: string): void {
    const out = $("#console-out");
    out.textContent += (out.textContent ? "\n" : "") + msg;
    out.scrollTop = out.scrollHeight;
  }

  private log(msg: string): void {
    const out = $("#event-log");
    const time = new Date().toLocaleTimeString(undefined, { hour12: false });
    out.textContent += `${time} ${msg}\n`;
    out.scrollTop = out.scrollHeight;
    // Keep the log bounded.
    const lines = out.textContent.split("\n");
    if (lines.length > 300) out.textContent = lines.slice(-200).join("\n");
  }

  private setActivity(msg: string): void {
    $("#activity").textContent = msg;
  }
}

function short(k: string): string {
  return k.length > 12 ? k.slice(0, 12) + "…" : k;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** First 256 bits of the filter rendered as a 0/1 strip. */
function renderBloomPreview(reader: { bloomPreview(n?: number): Uint8Array; bloomBits: number }): string {
  const bytes = reader.bloomPreview(32);
  let s = "";
  for (const b of bytes) s += b.toString(2).padStart(8, "0");
  return s + (reader.bloomBits > 256 ? "…" : "");
}
